/**
 * FleetManager V3 - Quick Actions Widget
 * Contextual action buttons based on profile V3
 *
 * V3: Uses profile.quick_actions array for filtering.
 * Falls back to profile-based filtering for backward compatibility.
 * Hides entirely if profile is read-only.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Car, Users, Fuel, Wrench, AlertTriangle, FileText, BarChart3, Zap, MapPin, KeyRound } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '../../shared/index.js';
import { useProfile } from '../../../hooks/useProfile.js';

// Libellés : dashboard.quickActions.items.<id>
const ALL_ACTIONS = [
  { id: 'add_vehicle', icon: Car },
  { id: 'add_driver', icon: Users },
  { id: 'view_sites', icon: MapPin },
  { id: 'add_fuel', icon: Fuel },
  { id: 'add_maintenance', icon: Wrench },
  { id: 'declare_claim', icon: AlertTriangle },
  { id: 'add_document', icon: FileText },
  { id: 'run_report', icon: BarChart3 },
  { id: 'goto_pool', icon: KeyRound },
];

export function QuickActionsWidget({ onAction }) {
  const { t } = useTranslation();
  const { profile, isReadOnly } = useProfile();

  // V3: If read-only, don't show quick actions at all
  if (isReadOnly) return null;

  // V3: Use profile.quick_actions if available, else show all
  const quickActionIds = profile?.quick_actions;
  const visibleActions = (Array.isArray(quickActionIds) && quickActionIds.length > 0)
    ? ALL_ACTIONS.filter(action => quickActionIds.includes(action.id))
    : ALL_ACTIONS;

  if (visibleActions.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Zap} iconColor="text-yellow-400">{t('dashboard.quickActions.title')}</CardTitle>
      </CardHeader>

      <div className="grid grid-cols-2 gap-2">
        {visibleActions.map(action => (
          <button
            key={action.id}
            onClick={() => onAction?.(action.id)}
            className="flex items-center gap-2 p-2 rounded-lg bg-gray-100 dark:bg-slate-700/30 hover:bg-gray-200 dark:hover:bg-slate-700/50 transition-colors group"
          >
            <action.icon size={14} className="text-gray-500 dark:text-slate-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors" />
            <span className="text-xs text-gray-700 dark:text-slate-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
              {t(`dashboard.quickActions.items.${action.id}`)}
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
}

export default QuickActionsWidget;
